import { Form, Row, Col, InputGroup, Modal, Button, Alert } from 'react-bootstrap'
import { handleBinaryFile } from '../article'
import * as Yup from 'yup'
import type { FormikErrors, FormikTouched } from 'formik'
import type { AlertState } from '../common'
import type { Media } from './types'

type Values = {
  mediaId: number
  name: string
  settingTemplate: string | null
  settingFile: string | null
  settingFilename: string | null
}

type EnumValues = {
  settingTemplates: Record<string, string>
}

type Props = {
  values: Values
  errors: FormikErrors<Values>
  touched: FormikTouched<Values>
  handleChange: React.ChangeEventHandler<HTMLInputElement>
  handleBlur: React.FocusEventHandler<HTMLInputElement>
  handleSubmit: React.SubmitEventHandler<HTMLFormElement>
  setFieldValue: (field: string, value: unknown) => void
  isValid: boolean
  fetching: boolean
  onClickCancel: () => void
  title: string
  submitButtonText: string
  mediaList?: Pick<Media, 'id' | 'name'>[]
  alert: AlertState | null
  isSettingWritable: boolean
  enumValues: EnumValues
  canShowExperimentalFeatures: boolean
}

const _SettingUpsertForm = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  handleSubmit,
  setFieldValue,
  isValid,
  fetching,
  onClickCancel,
  title,
  submitButtonText,
  mediaList,
  alert,
  isSettingWritable,
  enumValues,
  canShowExperimentalFeatures
}: Props) => {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const loadedBinaryAsync = handleBinaryFile(file)

    loadedBinaryAsync.then((value) => {
      setFieldValue('loadedBinaryFile', value)
    })

    handleChange(e)
  }
  return (
    <>
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      {alert?.show && alert?.alertBody && (
        <Alert className='upsert-setting-errors-alert' variant='danger'>
          {alert.alertBody}
        </Alert>
      )}
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group as={Row} className='mb-3'>
            <Form.Label column className='col-3'>
              媒体名
            </Form.Label>
            <Col>
              <Form.Control
                as='select'
                type='text'
                name='mediaId'
                value={values.mediaId}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={!!(touched.mediaId && errors.mediaId)}
                disabled={(mediaList?.length ?? 0) <= 1}
              >
                {mediaList?.map(({ id, name }) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type='invalid'>{errors.mediaId}</Form.Control.Feedback>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className='mb-3'>
            <Form.Label column className='col-3'>
              名称
            </Form.Label>
            <Col>
              <Form.Control
                type='text'
                name='name'
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={!!(touched.name && errors.name)}
              />
              <Form.Control.Feedback type='invalid'>{errors.name}</Form.Control.Feedback>
            </Col>
          </Form.Group>
          {canShowExperimentalFeatures && (
            <Form.Group as={Row} className='mb-3'>
              <Form.Label column className='col-3'>
                紙面ベーステンプレート
              </Form.Label>
              <Col>
                <Form.Control
                  as='select'
                  type='text'
                  name='settingTemplate'
                  value={values.settingTemplate || ''}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  isInvalid={!!(touched.settingTemplate && errors.settingTemplate)}
                  disabled={!isSettingWritable}
                >
                  {Object.keys(enumValues.settingTemplates).map((k) => (
                    <option key={k} value={k}>
                      {enumValues.settingTemplates[k]}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback type='invalid'>{errors.settingTemplate}</Form.Control.Feedback>
              </Col>
            </Form.Group>
          )}
          <Form.Group as={Row} className='mb-3'>
            <Form.Label column className='col-3'>
              紙面ベースファイル
            </Form.Label>
            <Col>
              <InputGroup hasValidation>
                <Form.Control
                  id='settingFileInput'
                  name='settingFile'
                  type='file'
                  accept='.zip'
                  onChange={handleFileChange}
                  onBlur={handleBlur}
                />
              </InputGroup>
            </Col>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={onClickCancel}>
            キャンセル
          </Button>
          <Button type='submit' disabled={!isValid || fetching}>
            {submitButtonText}
          </Button>
        </Modal.Footer>
      </Form>
    </>
  )
}

export const SettingUpsertForm = Object.assign(_SettingUpsertForm, {
  schema: Yup.object().shape({
    name: Yup.string().required(),
    mediaId: Yup.number().required(),
    settingTemplate: Yup.string().nullable(),
    settingFilename: Yup.string().nullable()
  })
})
